import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import axios from 'axios';
import { 
  Instagram, Linkedin, Twitter, Mail, 
  Send, Check, Loader2, ArrowUp, MessageCircle,
  Handshake, TrendingUp, Mic, Briefcase, Settings
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const quickLinks = [
  { name: 'About', href: '#about' },
  { name: 'Work', href: '#work' },
  { name: 'Packages', href: '#packages' },
  { name: 'Insights', href: '#insights' },
  { name: 'Contact', href: '#contact' },
];

const inquiryTypes = [
  'Discovery Call',
  'Strategic Advisory',
  'Full Handholding',
  'Partnership',
  'Media'
];

const contactTiles = [
  { icon: Handshake, title: 'Partner With Us', description: 'Strategic alliances' },
  { icon: TrendingUp, title: 'Investment', description: 'Capital opportunities' },
  { icon: Mic, title: 'Media & Speaking', description: 'Thought leadership' },
  { icon: Briefcase, title: 'Collaboration', description: 'Business ventures' },
];

const countries = ['Nigeria', 'Zimbabwe', 'Ghana', 'Kenya', 'Tanzania', 'Uganda', 'Ethiopia', 'Senegal', 'DRC'];

export const Footer = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiry_type: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Thank you! Your message has been sent.');
      setFormData({ name: '', email: '', company: '', inquiry_type: '', message: '' });
      setTimeout(() => setIsSubmitted(false), 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="contact" className="footer pt-24 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6" ref={ref}>
        {/* Final CTA Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Build Something Real? <span className="gradient-text">Let's Talk.</span>
          </h2>
          <p className="text-white/60 max-w-xl mx-auto">
            Start with a Discovery Call — it's the fastest way to get clarity.
          </p>
        </motion.div>

        {/* Contact Tiles */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12"
        >
          {contactTiles.map((tile, index) => (
            <div 
              key={tile.title}
              className="glass-light rounded-xl p-4 text-center card-hover cursor-pointer"
              onClick={() => scrollToSection('#contact-form')}
            >
              <tile.icon size={24} className="text-[#D4AF37] mx-auto mb-2" />
              <p className="text-sm font-medium text-white">{tile.title}</p>
              <p className="text-xs text-white/50">{tile.description}</p>
            </div>
          ))}
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 mb-16">
          {/* Left - Contact Form (Simplified) */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            id="contact-form"
          >
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">Get In Touch</p>
            
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              {/* Simplified: Name, Email, Inquiry Type, Message */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name *"
                    required
                    className="input-modern"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address *"
                    required
                    className="input-modern"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div>
                <select
                  name="inquiry_type"
                  value={formData.inquiry_type}
                  onChange={handleChange}
                  required
                  className="input-modern bg-transparent"
                  data-testid="input-inquiry-type"
                >
                  <option value="" disabled>What are you interested in? *</option>
                  {inquiryTypes.map((type) => (
                    <option key={type} value={type} className="bg-[#121212]">{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="What's on your mind? *"
                  required
                  rows={4}
                  className="input-modern resize-none"
                  data-testid="input-message"
                />
              </div>

              <div className="flex flex-wrap gap-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Sending...
                    </>
                  ) : isSubmitted ? (
                    <>
                      <Check size={18} />
                      Sent!
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
                
                {/* WhatsApp Button - Critical for India + Africa audience */}
                <a
                  href="https://wa.me/919899969196"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary flex items-center gap-2"
                  data-testid="whatsapp-button"
                >
                  <MessageCircle size={18} />
                  Chat on WhatsApp
                </a>
              </div>
            </form>
          </motion.div>

          {/* Right - Links & Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="lg:pl-12"
          >
            {/* Quick Links */}
            <div className="mb-8">
              <p className="text-white/50 text-sm uppercase tracking-widest mb-4">Quick Links</p>
              <div className="flex flex-wrap gap-4">
                {quickLinks.map((link) => (
                  <button
                    key={link.name}
                    onClick={() => scrollToSection(link.href)}
                    className="footer-link"
                  >
                    {link.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Social Links */}
            <div className="mb-8">
              <p className="text-white/50 text-sm uppercase tracking-widest mb-4">Follow</p>
              <div className="flex gap-4">
                <a
                  href="https://instagram.com/businesswisdombykirit"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-[#E4405F]/20 hover:text-[#E4405F] transition-colors"
                  data-testid="social-instagram"
                >
                  <Instagram size={20} />
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-[#0A66C2]/20 hover:text-[#0A66C2] transition-colors"
                  data-testid="social-linkedin"
                >
                  <Linkedin size={20} />
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-white/10 hover:text-white transition-colors"
                  data-testid="social-twitter"
                >
                  <Twitter size={20} />
                </a>
                <a
                  href="mailto:contact@kiritsobti.com"
                  className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-[#D4AF37]/20 hover:text-[#D4AF37] transition-colors"
                  data-testid="social-email"
                >
                  <Mail size={20} />
                </a>
              </div>
            </div>

            {/* Times of Africa Subscribe */}
            <div className="glass-light rounded-xl p-6 mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/8yod4ui6_d5b91698-f93e-4168-84d3-01ff07a1b57b.jpeg"
                alt="The Times of Africa"
                className="h-10 w-auto object-contain mb-3"
              />
              <p className="text-sm text-white/50 mb-4">
                Subscribe to the monthly magazine covering Africa-India relations.
              </p>
              <button className="btn-secondary text-sm w-full">
                Subscribe Now
              </button>
            </div>

            {/* Book Discovery Call CTA */}
            <button
              onClick={() => scrollToSection('#packages')}
              className="btn-primary w-full"
            >
              Book a Discovery Call
            </button>
          </motion.div>
        </div>

        {/* Countries Served Strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.4 }}
          className="text-center mb-8"
        >
          <p className="text-xs text-white/40 mb-2">Countries Served</p>
          <div className="flex flex-wrap justify-center gap-2">
            {countries.map((country) => (
              <span key={country} className="text-xs text-white/30">{country}</span>
            ))}
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-center sm:text-left flex items-center gap-3">
            <div>
              <p className="text-xl font-bold">
                <span className="text-white">Kirit</span>
                <span className="text-[#D4AF37]">Sobti</span>
              </p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-white/40">
                  © {new Date().getFullYear()} All rights reserved
                </p>
                <button
                  onClick={() => window.location.href = '/admin'}
                  className="w-5 h-5 rounded flex items-center justify-center text-white/20 hover:text-white/40 transition-colors"
                  title="Admin"
                  data-testid="admin-secret-btn"
                >
                  <Settings size={12} />
                </button>
              </div>
            </div>
          </div>

          <button
            onClick={scrollToTop}
            className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-colors"
            data-testid="scroll-to-top"
          >
            <ArrowUp size={20} />
          </button>
        </div>
      </div>
    </footer>
  );
};
